import type { SearchHistoryEntry, SavedStarredList } from '../types';
import { KW_SORT_PARAM } from './keywordViewParams';

/**
 * Replay target for a recorded search, or null when it has no replayable URL.
 * ``?mode=`` selects the surface, so an entry recorded without it must not
 * replay into the keyword default.
 */
export function replayPath(entry: Pick<SearchHistoryEntry, 'mode' | 'payload'>): string | null {
  const url = (entry.payload as { url?: unknown } | undefined)?.url;
  if (!url || typeof url !== 'string') return null;
  try {
    const target = new URL(url, 'http://replay.invalid');
    target.searchParams.set('mode', entry.mode === 'keyword' ? 'keyword' : 'ai');
    return target.pathname + target.search;
  } catch {
    return null;
  }
}

/** Keyword-compare URL that reopens a saved starred list, or null without valid drug ids. */
export function savedListPath(
  list: Pick<SavedStarredList, 'id' | 'name' | 'drugs' | 'search_context'>,
): string | null {
  const ids = list.drugs.map((d) => d.drug_id).filter((n): n is number => Number.isFinite(n));
  if (!ids.length) return null;
  const kw = list.search_context?.label
    ? `&kw=${encodeURIComponent(list.search_context.label)}`
    : '';
  // saved=1 → open on the rail's Starred tab; stars AND columns (first 100) are
  // the list, fully editable. The search re-runs from the handed-off kw-request.
  // listId/listName → editing the restored list "Update"s it in place
  // instead of saving a brand-new one.
  const lid = `&listId=${list.id}&listName=${encodeURIComponent(list.name)}`;
  return `/compare-keyword?stars=${ids.join(',')}&panels=${ids.slice(0, 100).join(',')}&saved=1${lid}${kw}`;
}

/**
 * Restore (or clear) the originating keyword request in sessionStorage so the
 * compare rail + highlighting rebuild. Storage-only; callers navigate after.
 */
export function handOffSavedList(
  list: Pick<SavedStarredList, 'search_context'>,
  storageKey: (k: string) => string,
): void {
  try {
    if (list.search_context?.payload) {
      sessionStorage.setItem(storageKey('kw-request'), JSON.stringify(list.search_context.payload));
    } else {
      sessionStorage.removeItem(storageKey('kw-request'));
    }
  } catch {
    /* sessionStorage may be unavailable; restore still works minus highlighting */
  }
}

/**
 * Keyword-compare URL carrying the active view (kw / group / refine / sort) and,
 * when any rows are ticked, `panels` in the given (tick) order. Empty values are
 * omitted; no sorting and no cap (the cap is the UI's job).
 */
export function compareKeywordPath(o: {
  kw?: string; group?: string; refine?: string; sort?: string; panels?: readonly number[];
}): string {
  const params = new URLSearchParams();
  if (o.kw) params.set('kw', o.kw);
  if (o.group) params.set('group', o.group);
  if (o.refine) params.set('refine', o.refine);
  if (o.sort) params.set(KW_SORT_PARAM, o.sort);
  if (o.panels?.length) params.set('panels', o.panels.join(','));
  const qs = params.toString();
  return `/compare-keyword${qs ? `?${qs}` : ''}`;
}
